import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { DueSoonReminders } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type DueRemindersStatus = "loading" | "ready" | "error";

export interface UseDueRemindersResult {
  /** Null until the request resolves, or if it failed. */
  reminders: DueSoonReminders | null;
  status: DueRemindersStatus;
  error: string | null;
}

/** How many reminders the Home tile lists; the counts still cover every due/soon one. */
export const DUE_REMINDERS_LIMIT = 5;

/**
 * ef-061-9 — reads GET /api/demo/reminders/due-soon?limit=5 for the Home Reminders tile.
 * Re-reads every time the tile mounts (no module cache), so a reminder completed or a
 * machine archived on the Equipment page is reflected on the next visit to Home.
 * Same shape as useCashFlowPlanner. A "ready" result with both counts 0 is a successful
 * answer meaning nothing is due — the tile renders its empty state, never a spinner.
 */
export function useDueReminders(): UseDueRemindersResult {
  const [reminders, setReminders] = useState<DueSoonReminders | null>(null);
  const [status, setStatus] = useState<DueRemindersStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getDueSoonReminders(DUE_REMINDERS_LIMIT, controller.signal);
        if (cancelled) return;
        setReminders(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setReminders(null);
        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return { reminders, status, error };
}
